import * as THREE from 'three';
import DeleteTimelineElementAction from '../../actions/timelineEditor/DeleteTimelineElementAction';
import AddTimelineElementAction from '../../actions/timelineEditor/AddTimelineElementAction';
import ReverseTimelineElementAction from '../../actions/timelineEditor/ReverseTimelineElementAction';
import Engine from '../../classes/Engine';
import SetTimelineObjectReferenceAction from '../../actions/timelineEditor/SetTimelineObjectReferenceAction';
import AddMultipleTimelineElementsAction from '../../actions/timelineEditor/AddMultipleTimelineElementsAction';
import Utils from '../../classes/Utilities';
import ARTracker from '../../classes/ARTracker';
import {EffectorTitleMap, timelineElementMap} from '../../classes/timeline';
import TimelineObject from '../../classes/timeline/TimelineObject';
import {TimelineActionTrackFragment, TimelineFragment} from '../../classes/timeline/TimelineFragment';
import store, {StoreActions} from '../../state/store';
import TimelineTrack from '../../classes/timeline/TimelineTrack';
import TimelineChannel from '../../classes/timeline/TimelineChannel';
import ProjectSaver from '../../classes/ProjectSaver';
import {SaveModificationTypes, TimelineEffectorTypes, TimelineTrackTypes,} from '../../globals'
import {assert} from '../../classes/utilities/ErrorHandling';
import MathUtils from '../../classes/utilities/MathUtils';
import {ObjectTriggerTypes} from "../../interfaces/enums";
import TextUtils from '../../classes/utilities/TextUtils';
import {IContextMenuItem, IVector2} from '../../interfaces/Misc';
import {ITimelineElementParent, TimelineElementTypes} from '../../interfaces/Timeline';
import {Timeline} from "../../classes/timeline/Timeline";

/**
 * contains a collection of utility functions specific to the timeline
 * @export
 * @class TimelineEditorUtilities
 */
export default class TimelineEditorUtilities
{
	/**
	 * calculates the timeline time at the mouse position using frame intervals and taking scroll offset into account
	 *
	 * inteded for use with the timeline's timebarDisplay or Timestrip canvases
	 * @static
	 * @param {HTMLCanvasElement} canvas the canvas used for calculation, assumed to be a relevant timline editor canvas
	 * @param {{x: Number, y: Number}} mousePosition the mouse coordinates relative to the given canvas
	 * @param {Number} maxPossibleVal (optional) is a temporary patch to allow for bigger max values, since for some reason totalTime isn't retrieved properly from within TimelineEditorUtilities
	 * @param {{ maxPossibleVal: 0, ceil: false }} params additional parameters to help the function work
	 * @returns the nearest frame number calculated from the mouse position within the canvas
	 * @memberof TimelineEditorUtilities
	 */
	static getFrameAtPosition(canvas: HTMLCanvasElement, mousePosition: IVector2, params?: { maxPossibleVal?: number, ceil?: boolean }) 
	{
		const options = { maxPossibleVal: 0, ceil: false, ...params };

		const visibleStartFrame = store.getState().timelineModule.editorData.visibleStartFrame;
		const visibleEndFrame = store.getState().timelineModule.editorData.visibleEndFrame;
		const frameInterval = store.getState().timelineModule.editorData.frameInterval;

		if (!canvas)
			return 0;

		const rect = canvas.getBoundingClientRect();
		const normalizedPosition = {
			x: ((mousePosition.x - rect.left) / rect.width),
			// y: Utils.clamp01((mousePosition.y - rect.top) / rect.height)
		};
		//console.log(normalizedPosition);

		const canvasPosiiton = (visibleEndFrame - visibleStartFrame) * normalizedPosition.x;
		const exactFrame = options.ceil ? Math.ceil(canvasPosiiton) : Math.round(canvasPosiiton);
		//console.log(targetFrame);

		const intervalRemainder = exactFrame % frameInterval;
		const roundUp = intervalRemainder > frameInterval * 0.5;
		//console.log(intervalRemainder + " | " + roundUp);

		const roundedFrame = roundUp ? exactFrame + (frameInterval - intervalRemainder) : exactFrame - intervalRemainder;

		//console.log(roundedFrame);
		const targetEndFrame = Math.max(visibleEndFrame,
			//Note: Before introducing totalTime the max time value was loopTime+1.
			// maxPossibleVal is used to enable passing the totalTime value from outside, since
			// for some reason TimelineEditorUtilities keeps overriding this value
			Math.max(options.maxPossibleVal,
				//(TimelineEditorUtilities.getTimelineLoopTime() + 1) * TimelineEditorUtilities.getTimelineFps()
				roundedFrame + 1
			)
		);

		return MathUtils.clampScalar(visibleStartFrame + roundedFrame, 0, targetEndFrame);
	}

	/**
	 * @static
 	 * @param {Number} time time in seconds
	 * @returns the nearest frame number of the given time in seconds
	 * @memberof TimelineEditorUtilities
	 */
	static getFrameFromTime(time: number) 
	{
		const timelineId = store.getState().timelineModule.editorData.timelineId;
		if (!timelineId) return 0;
		const timeline = timelineElementMap.get(timelineId) as Timeline;
		const fps = timeline? timeline.fps : 30;
		return Math.round(time * fps);
	}

	/**
	 * @static
	 * @param {number} frame the number of a frame
	 * @returns the time in seconds calculated from the given frame number
	 * @memberof TimelineEditorUtilities
	 */
	static getTimeFromFrame(frame: number) 
	{
		const timelineId = store.getState().timelineModule.editorData.timelineId;
		if (!timelineId) return 0;
		const timeline = timelineElementMap.get(timelineId) as Timeline;
		const fps = timeline? timeline.fps : 30;
		return frame / fps;
	}

	/**
	 * sorting function that uses the given element's startTime as the condition
	 * @static
	 * @param {TimelineFragment} a baseline fragment to compare
	 * @param {TimelineFragment} b target fragment to compare with
	 * @returns -1 if a starts before b; 1 if a starts after b; 0 if both a and b start at the same time
	 * @memberof TimelineEditorUtilities
	 */
	static sortByStartTime(a: TimelineFragment, b: TimelineFragment) 
	{
		if (a.startTime === b.startTime) 
		{
			if (a.channel.id === b.channel.id) return -1;
			const track = a.timeline.objectsTrack;
			assert(track?.objects);
			//@ts-ignore assert above should make sure that track is not undefined but TS doesn't seem to understand that
			for (const object of track.objects) 
			{
				for (const channel of object.channels) 
				{
					if (channel.id === a.channel.id) return -1;
					if (channel.id === b.channel.id) return 1;
				}
			}
			assert(0);
		}

		return a.startTime < b.startTime ? -1 : a.startTime > b.startTime ? 1 : 0;
	}

	/**
	 * look for the startTime value of the next timebar in line starting from 'fromTime'
	 * @static
 	 * @param {Number} fromTime a current time used to start the search from
	 * @returns the startTime of the next timeline timebar from all objects and channels
	 * @memberof TimelineEditorUtilities
	 */
	static getNextTimebarStartTime(fromTime: number) 
	{
		const timelineId = store.getState().timelineModule.editorData.timelineId;
		if(!timelineId) return;
		const timeline = timelineElementMap.get(timelineId) as Timeline;
		if(!timeline) return;
		timeline.sortFragments();
		
		for (const fragment of timeline.fragments)
		{
			if (fragment.startTime > fromTime) 
			{
				return fragment.startTime;
			}
		}

		return fromTime;
	}

	/**
	 * look for the startTime of the previous timebar in line starting from 'fromTime'
	 * @static
 	 * @param {Number} fromTime a current time used to start the search from
	 * @returns the startTime of the previous timeline timebar from all objects and channels
	 * @memberof TimelineEditorUtilities
	 */
	static getPreviousTimebarStartTime(fromTime: number) 
	{
		const timelineId = store.getState().timelineModule.editorData.timelineId;
		if (!timelineId) return fromTime;
		const timeline = timelineElementMap.get(timelineId) as Timeline;
		if(!timeline) return;
		timeline.sortFragments();

		for(let i = timeline.fragments.length -1; i >= 0; i--)
		{
			const fragment = timeline.fragments[i];
			if (fragment.startTime < fromTime) 
			{
				return fragment.startTime;
			}
		}

		return fromTime;
	}

	/**
	 * helper function to add multiple objects to the timeline
	 *
	 * *TimelineTrack is a legacy concept, but required for now until we refactor it out*
	 * @static
	 * @param {TimelineTrack} track the TimelineTrack to add the new TimelineObjects to
 	 * @param {[THREE.Object3D]} sceneObjects an array of scene objects for which to create the TimelineObjects that will be added to the given track
	 * @memberof TimelineEditorUtilities
	 */
	static addSceneObjectsToTrack(track: TimelineTrack, sceneObjects: THREE.Object3D[])
	{
		const timelineObjects = sceneObjects.filter(object =>
		{
			if (!object || object instanceof ARTracker) return false;

			for (const timelineObject of track.objects) 
			{
				if (timelineObject.hidden) continue;
				if (timelineObject.sceneObjectGuid === object.userData.guid) 
				{
					return false;
				}
			}

			ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Reference);
			return true;
		}).map(sceneObject => { return new TimelineObject({ track: track, sceneObjectGuid: sceneObject.userData.guid }) });

		new AddMultipleTimelineElementsAction(timelineObjects.map(timelineObject => { return { parent: track, element: timelineObject } }));
	}

	/**
	 * helper function to add an object group to the timeline
	 *
	 * *TimelineTrack is a legacy concept, but required for now until we refactor it out*
	 * @static
	 * @param {TimelineTrack} track the TimelineTrack to add the new TimelineObjects to
 	 * @param {String} objectGroupGuid the guid of the object group to for which to create the TimelineObject that will be added to the given track
	 * @memberof TimelineEditorUtilities
	 */
	static addObjectGroupToTrack(track: TimelineTrack, objectGroupGuid: string) 
	{
		let exists = false;
		for (const timelineObject of track.objects) 
		{
			if (timelineObject.hidden)
				continue;

			if (timelineObject.objectGroupGuid === objectGroupGuid) 
			{
				exists = true;
				break;
			}
		}

		if (!exists) 
		{
			const newObject = new TimelineObject({ track: track, objectGroupGuid: objectGroupGuid });
			new AddTimelineElementAction(track, newObject);
		}
	}

	/**
	 * generates an array of context menu items for adding a new fragment to the timeline, where each menu entry corresponds to a TimelineEffector type
	 * @static
	 * @param {TimelineChannel} channel the channel to which the newly created fragment will be added
	 * @param {number} [startingTime=0] what the startTime value of the new fragment should be
	 * @param {number} [duration=1] what the duration of the new fragment should be (endTime = startTime + duration)
	 * @returns an array of data objects required for the ContextMenu to properly render each fragment entry
	 * @memberof TimelineEditorUtilities
	 */
	static generateAddFragmentMenu(channel: TimelineChannel, startingTime = 0, duration?: number) 
	{
		const output = new Array<any>();
		const timelineId = store.getState().timelineModule.editorData.timelineId;
		if (!timelineId) return output;
		const timeline = timelineElementMap.get(timelineId) as Timeline;

		if (channel.object.track.type === TimelineTrackTypes.ActionsTrack) 
		{
			const eventTypes = [ObjectTriggerTypes.NextStep, ObjectTriggerTypes.PreviousStep, ObjectTriggerTypes.MoveToStep, ObjectTriggerTypes.Alignment];
			const eventNames = [TextUtils.prettifyText(ObjectTriggerTypes.NextStep), TextUtils.prettifyText(ObjectTriggerTypes.PreviousStep), TextUtils.prettifyText(ObjectTriggerTypes.MoveToStep), "Content Placement"];
			for (let i = 0; i < eventTypes.length; i++) 
			{
				const eventType = eventTypes[i];
				output.push(
					{
						renderObject: eventNames[i],
						id: "new-fragment-" + eventType,
						callback: (channel: TimelineChannel, fragmentType) => 
						{
							duration = 1 / timeline.fps;

							const proposedName = TextUtils.prettifyText(eventType);
							const newFragment = new TimelineActionTrackFragment(Utils.getUniqueElementName(channel.fragments, proposedName), channel, fragmentType, startingTime, startingTime + duration);
							newFragment.effector.target = eventType;
							new AddTimelineElementAction(channel, newFragment);
						},
						callbackParams: [channel, TimelineEffectorTypes.EventInvocation, eventType],
					}
				);
			}
		}
		else 
		{
			for (const typeKey of Object.keys(TimelineEffectorTypes)) 
			{
				if (TimelineEffectorTypes[typeKey] === TimelineEffectorTypes.TransformRotate) continue; //deprecated animation type. we support playing those, but not creating them
				if (TimelineEffectorTypes[typeKey] === TimelineEffectorTypes.ObjectAnimation && channel.object.objectGroupGuid) continue;
				output.push(
					{
						renderObject: EffectorTitleMap.get(TimelineEffectorTypes[typeKey]),
						id: "new-fragment-" + typeKey,
						callback: (channel: TimelineChannel, fragmentType: TimelineEffectorTypes) =>
						{
							if (duration === undefined) 
							{
								duration = store.getState().preferencesModule.editorPreferences.defaultTimebarLength || 1;
							}

							if (TimelineEffectorTypes[typeKey] === TimelineEffectorTypes.EventInvocation) 
							{
								duration = 1 / timeline.fps;
							}

							const proposedName = EffectorTitleMap.get(fragmentType);
							if ( !proposedName)
							{
								console.error("Fragment type not found: ", fragmentType);
								return;
							}
							const newFragment = new TimelineFragment(Utils.getUniqueElementName(channel.fragments, proposedName), channel, fragmentType, startingTime, startingTime + (duration ?? 1));
							new AddTimelineElementAction(channel, newFragment);
						},
						callbackParams: [channel, TimelineEffectorTypes[typeKey]],
					}
				);
			}
		}

		return output;
	}

	/**
	 * given an ID list, retrieve timeline elements / clones of these elements / IDs of the clones
	 * @static
 	 * @param {Array } string the IDs of the elements to retrieve
 	 * @param {Boolean} bClone whether to produce clones
 	 * @param {Boolean} bFetchID whether to return the IDs of the clones instead of the actual elements
	 * @returns an array of elements or array of IDs of clones of elements
	 * @memberof TimelineEditorUtilities
	 */
	static getElementsByIDList(objectIDs: string[], bClone = false, bFetchID = false) 
	{
		const arFetchedElements = new Array<any>();
		objectIDs.forEach((s) => 
		{
			const el = timelineElementMap.get(s) as TimelineTrack | TimelineChannel | TimelineObject | TimelineFragment;
			if (el && el.getParent)
			{
				const fetchedElement = bClone ? el.clone(el.getParent()) : el;
				arFetchedElements.push(bFetchID ? fetchedElement.id : fetchedElement);
			}
		});

		return arFetchedElements;
	}

	/**
	 * delete all selected elements from the timeline
	 * @static
	 * @memberof TimelineEditorUtilities
	 */
	static deleteSelectedElements() 
	{
		const selectedIDs = store.getState().timelineModule.editorData.selectedElementIDs;

		if (Array.isArray(selectedIDs) && selectedIDs.length) 
		{
			const arElements = TimelineEditorUtilities.getElementsByIDList(selectedIDs);
			TimelineEditorUtilities.deleteElements(arElements);
		}
	}

	/**
	 * delete all passed elements from the timeline
	 * @static
	 * @memberof TimelineEditorUtilities
	 */
	static deleteElements(arElements: any[]) 
	{
		new DeleteTimelineElementAction(arElements);
	}

	/**
	 * generates an array of grouped context menu items, specifically for the given TimelineElement's type and the TimelineEditor's state
	 * @static
 	 * @param {Array | TimelineFragment | TimelineChannel | TimelineObject | TimelineTrack | Timeline} element the TimelineElement for which to generate the context menu entries, or an array of IDs of elements
 	 * @param {Number} atFrame used to calculate the startTime of newly-created TimelineFragments, if required
	 * @returns an array of grouped context menu items for the given element
	 * @memberof TimelineEditorUtilities
	 */
	static generateElementContextMenu(elementIds: string[], atFrame?: number): IContextMenuItem[] 
	{
		if (!Array.isArray(elementIds) || !elementIds.length) return [];

		const clipboardItemIds = store.getState().timelineModule.editorData.clipboard;
		const clipboard = clipboardItemIds.map(itemId => { return timelineElementMap.get(itemId) });
		const pluralSuffix = elementIds.length > 1 ? "s" : "";
		const elements = elementIds.map(id => timelineElementMap.get(id));
		const elementType = elements[0]?.getType();
		let elementTypeDisplayName = elementType;

		if (elementTypeDisplayName === TimelineElementTypes.Fragment) 
		{
			let isClickingActions = true;
			for (const element of elements) 
			{
				if (element instanceof TimelineFragment && element.effectorType !== TimelineEffectorTypes.EventInvocation) 
				{
					isClickingActions = false;
				}
			}
			if (isClickingActions) 
			{
				elementTypeDisplayName = TimelineElementTypes.ActionTrackFragment;
			}
		}

		// all elements should have this
		let menuItems = new Array<IContextMenuItem>();
		menuItems.push(
			{
				renderObject: "Delete " + elementTypeDisplayName + pluralSuffix,
				id: "Delete " + elementTypeDisplayName,
				callback: () => 
				{
					TimelineEditorUtilities.deleteSelectedElements();
				},
			},
			{
				renderObject: "Copy " + elementTypeDisplayName + pluralSuffix,
				id: "Copy " + elementTypeDisplayName,
				callback: () => 
				{
					store.dispatch(StoreActions.timeline.setClipboard(TimelineEditorUtilities.getElementsByIDList(elementIds, true, true)));
				},
			},
		);

		switch (elementType) 
		{
			case TimelineElementTypes.Fragment:
			case TimelineElementTypes.ActionTrackFragment:
				menuItems.push(
					{
						renderObject: "Cut " + elementTypeDisplayName + pluralSuffix,
						id: "Cut " + elementTypeDisplayName,
						callback: () => 
						{
							store.dispatch(StoreActions.timeline.setClipboard(TimelineEditorUtilities.getElementsByIDList(elementIds, true, true)));
							TimelineEditorUtilities.deleteElements(elements);
						},
					}
				);
				menuItems.push(
					{
						renderObject: "Duplicate " + elementTypeDisplayName + pluralSuffix,
						id: "Duplicate " + elementTypeDisplayName,
						callback: () => 
						{
							const newFragments = new Array<TimelineFragment>();
							for (const fragment of elements) 
							{
								if (fragment instanceof TimelineFragment) 
								{
									const newFragment = fragment.clone(fragment.getParent?.());
									if (newFragment) 
									{
										newFragment.startTime = fragment.endTime;
										newFragment.endTime = newFragment.startTime + (fragment.endTime - fragment.startTime);
										newFragments.push(newFragment);
									}
								}
							}
							new AddTimelineElementAction(null, newFragments);
						},
					}
				);
				menuItems.push(
					{
						renderObject: "Edit " + elementTypeDisplayName + pluralSuffix,
						id: "Edit " + elementTypeDisplayName,
						callback: () => 
						{
							store.dispatch(StoreActions.timeline.setInspectedElement(true))
						},
					}
				);
				let timebarReversible = true;
				for (const element of elements) 
				{
					if (element instanceof TimelineFragment && !element.effector.isReversibleEffector()) 
					{
						timebarReversible = false;
						break;
					}
				}
				if (timebarReversible) 
				{
					menuItems.push({
						renderObject: "Reverse " + elementTypeDisplayName + pluralSuffix,
						id: "Reverse " + elementTypeDisplayName,

						callback: () => 
						{
							new ReverseTimelineElementAction(elements as any[]);
						},
					});
				}

				break;
			case TimelineElementTypes.Channel:
				const firstChannel = elements[0];
				if (firstChannel instanceof TimelineChannel && firstChannel.object.track.type === TimelineTrackTypes.ActionsTrack) 
				{
					menuItems = [];
				}
				else 
				{
					menuItems.push(
						{
							renderObject: "Duplicate " + elementTypeDisplayName + pluralSuffix,
							id: "Duplicate " + elementTypeDisplayName,
							callback: (channels: TimelineChannel[]) => 
							{
								const newChannels = new Array<TimelineChannel>();
								for (const channel of channels) 
								{
									if (channel instanceof TimelineChannel) 
									{
										newChannels.push(channel.clone(channel.object));
									}
								}
								new AddTimelineElementAction(null, newChannels);

							},
							callbackParams: [elements],
						},
					);
					menuItems.push(
						{
							renderObject: "Reverse " + elementTypeDisplayName + pluralSuffix,
							id: "Reverse " + elementTypeDisplayName,
							callback: (channels: TimelineChannel[]) => 
							{
								new ReverseTimelineElementAction(channels);
							},
							callbackParams: [elements],
						},
					);
				}

				if (clipboard.length && clipboard[0] instanceof TimelineFragment) 
				{
					const targetChannel = elements[0];
					const eligibleFragmentType = elements.length > 1 ? TimelineElementTypes.Fragment : //multiple channels selected imply these are all object track channels
						(targetChannel instanceof TimelineChannel && targetChannel.object.track.type === TimelineTrackTypes.ObjectsTrack) ? TimelineElementTypes.Fragment : TimelineElementTypes.ActionTrackFragment;
					const fragmentsToCopy = new Array<TimelineFragment>();
					for (const clipboardItem of clipboard) 
					{
						if (clipboardItem instanceof TimelineFragment && clipboardItem.getType() === eligibleFragmentType) 
						{
							fragmentsToCopy.push(clipboardItem);
						}
					}

					if (fragmentsToCopy.length) 
					{
						fragmentsToCopy.sort((a, b) => { return (a.startTime <= b.startTime) ? -1 : 1; });

						if (targetChannel instanceof TimelineChannel && targetChannel.object.track.type === TimelineTrackTypes.ActionsTrack) 
						{
							menuItems.push(
								{
									renderObject: "Paste " + eligibleFragmentType + (clipboard.length > 1 ? 's' : ''),
									id: "Paste " + eligibleFragmentType,
									callback: (channel, sourceFragments) => 
									{
										const newFragments = new Array<TimelineFragment>();
										const timeOffset = atFrame !== undefined ? TimelineEditorUtilities.getTimeFromFrame(atFrame) - sourceFragments[0].startTime : 0;
										for (const sourceFragment of sourceFragments) 
										{
											const newFragment = sourceFragment.clone(channel);
											newFragment.startTime += timeOffset;
											newFragment.endTime += timeOffset;
											newFragments.push(newFragment);
										}
										new AddTimelineElementAction(channel, newFragments);
									},
									callbackParams: [targetChannel, fragmentsToCopy],
								},
							);
						}
						else 
						{
							menuItems.push(
								{
									renderObject: "Paste " + eligibleFragmentType + (clipboard.length > 1 ? 's' : ''),
									id: "Paste " + eligibleFragmentType,
									callback: (channels: TimelineChannel[], sourceFragments: TimelineFragment[]) => 
									{
										const newFragments = new Array<TimelineFragment>();
										const timeOffset = atFrame !== undefined ? TimelineEditorUtilities.getTimeFromFrame(atFrame) - sourceFragments[0].startTime : 0;

										const timelineAdditions = new Array<{ parent: ITimelineElementParent, element: TimelineChannel | TimelineFragment }>();

										//sort fragments from top to bottom so the pasted result matches the source regardless of picking order
										sourceFragments.sort((a, b) => 
										{
											if (a.channel.id === b.channel.id) return -1;
											const track = a.timeline.objectsTrack;
											assert(track);
											// @ts-ignore assert above guarantees track is not null but typescript doesn't know that
											for (const object of track.objects) 
											{
												for (const channel of object.channels) 
												{
													if (channel.id === a.channel.id) return -1;
													if (channel.id === b.channel.id) return 1;
												}
											}

											return 0;
										});

										for (const channel of channels) 
										{
											const oldToTargetChannelMap: Map<TimelineChannel, TimelineChannel> = new Map();
											for (let i = 0; i < sourceFragments.length; i++) 
											{
												const sourceFragment = sourceFragments[i];
												if (!oldToTargetChannelMap.has(sourceFragment.channel)) 
												{
													let targetChannel: TimelineChannel;
													const currentChannelIndex = channel.object.channels.indexOf(channel);
													if (currentChannelIndex + i + 1 <= channel.object.channels.length) 
													{
														targetChannel = channel.object.channels[currentChannelIndex + i];
													}
													else 
													{
														targetChannel = new TimelineChannel(Utils.getUniqueElementName(channel.object.channels, "Channel"), channel.object);
														timelineAdditions.push({ parent: channel.object, element: targetChannel });
													}
													oldToTargetChannelMap.set(sourceFragment.channel, targetChannel);
												}

												const newFragment = sourceFragment.clone(oldToTargetChannelMap.get(sourceFragment.channel)!);
												timelineAdditions.push({ parent: oldToTargetChannelMap.get(sourceFragment.channel)!, element: newFragment });
												newFragment.startTime += timeOffset;
												newFragment.endTime += timeOffset;
												newFragments.push(newFragment);
											}
										}

										new AddMultipleTimelineElementsAction(timelineAdditions);
									},
									callbackParams: [elements, fragmentsToCopy],
								},
							);
						}
					}
				}
				break;
			case TimelineElementTypes.Object:
				menuItems.push(
					{
						renderObject: "Duplicate " + elementTypeDisplayName + pluralSuffix,
						id: "Duplicate " + elementTypeDisplayName,
						callback: (objectIDs) => 
						{
							const newObjects = TimelineEditorUtilities.getElementsByIDList(objectIDs, true);

							if (newObjects.length) 
							{
								new AddTimelineElementAction(null, newObjects);
							}
						},
						callbackParams: [elementIds],
					},
				);

				if (clipboard.length && clipboard[0]?.getType() === TimelineElementTypes.Channel) 
				{
					menuItems.push(
						{
							renderObject: "Paste " + TimelineElementTypes.Channel + (clipboard.length > 1 ? "s" : ""),
							id: "Paste " + TimelineElementTypes.Channel,
							callback: (objects: TimelineObject[], channelsToCopy: TimelineChannel[]) => 
							{
								const newChannels = new Array<TimelineChannel>();
								for (const object of objects) 
								{
									for (const channel of channelsToCopy) 
									{
										newChannels.push(channel.clone(object));
									}
								}
								new AddTimelineElementAction(null, newChannels);
							},
							callbackParams: [elements, clipboard]
						},
					);
				}

				if (elements.length === 1) 
				{
					const currentSelection = Engine.getInstance().getPickingManager().getCurrentSelection();
					const newReference = currentSelection.groupGuid ?? currentSelection.objects[0];

					if (newReference && !(newReference instanceof ARTracker)) 
					{
						menuItems.push(
							{
								renderObject: "Replace With Selected",
								id: "Replace With Selected",
								callback: (timelineObject, newReference) => 
								{
									new SetTimelineObjectReferenceAction(timelineObject, newReference);
								},

								callbackParams: [elements[0], newReference],
							},
						);
					}
				}

				break;
			case TimelineElementTypes.Track:
				{
					menuItems = [];

					if (elements[0] instanceof TimelineTrack && elements[0].type === TimelineTrackTypes.ObjectsTrack && clipboard.length && clipboard[0]?.getType() === TimelineElementTypes.Object) 
					{
						menuItems.push(
							{
								renderObject: "Paste " + TimelineElementTypes.Object + (clipboard.length > 1 ? "s" : ""),
								id: "Paste " + TimelineElementTypes.Object,
								callback: (track: TimelineTrack, objectsToCopy: TimelineObject[]) => 
								{
									const newObjects = new Array<TimelineObject>();
									for (const object of objectsToCopy) 
									{
										newObjects.push(object.clone(track));
									}
									new AddTimelineElementAction(null, newObjects);
								},
								callbackParams: [elements[0], clipboard],
							}
						);
					}
				}
				break;
			case TimelineElementTypes.Timeline:
			default:
				console.warn("unsupported element type: " + elementType);
				//console.log(elements);
				break;
		}

		return menuItems;
	}

	/**
	 * @static
	 * @param {TimelineChannel} channel the channel used for the search
	 * @returns the endTime of the last TimelineFragment in the given channel
	 * @memberof TimelineEditorUtilities
	 */
	static getLastFragmentEndTime(channel: TimelineChannel) 
	{
		const lastFragment = channel.fragments.length ? channel.fragments[channel.fragments.length - 1] : null;
		return lastFragment ? lastFragment.endTime : 0;
	}

	static findTimelineObjectFromSceneObjectGuid(objectGuid: string) 
	{
		const timelineId = store.getState().timelineModule.editorData.timelineId;
		if (!timelineId) return null;
		const timeline = timelineElementMap.get(timelineId) as Timeline;
		const track = timeline.objectsTrack;
		if ( track )
		{
			for (const object of track.objects)
			{
				if (object.sceneObjectGuid === objectGuid && !object.hidden) return object;
			}
		}

		return null;
	}

	static findTimelineObjectFromGroupGuid(groupGuid: string) 
	{
		const timelineId = store.getState().timelineModule.editorData.timelineId;
		if (!timelineId) return null;
		const timeline = timelineElementMap.get(timelineId) as Timeline;
		const track = timeline.objectsTrack;

		if ( track )
		{
			for (const object of track.objects)
			{
				if (object.objectGroupGuid === groupGuid && !object.hidden) return object;
			}
		}

		return null;
	}

	/**
	 * @static
	 * @returns the fps of the current timeline, or a default of 30 if it's undefined
	 * @memberof TimelineEditorUtilities
	 */
	static getTimelineFps() 
	{
		const timelineId = store.getState().timelineModule.editorData.timelineId;
		if (!timelineId) return 30;
		const timeline = timelineElementMap.get(timelineId) as Timeline;
		return timeline ? timeline.fps : 30;
	}

	/**
	 * @static
	 * @returns the loopTime of the current timeline, or a default of 10 if it's undefined
	 * @memberof TimelineEditorUtilities
	 */
	static getTimelineLoopTime() 
	{
		const timelineId = store.getState().timelineModule.editorData.timelineId;
		if (!timelineId) return 10;
		const timeline = timelineElementMap.get(timelineId) as Timeline;
		 return timeline ? timeline.loopTime : 10;
	}

	// /**
	//  * @static
	//  * @returns the totalTime of the current timeline, or a default of (loopTime + getMinExtraTime() seconds) if it's undefined
	//  * @memberof TimelineEditorUtilities
	//  */
	//  static getTimelineTotalTime()
	//  {
	// 	 const timelineId = store.getState().timelineModule.editorData.timelineId;
	// 	 const timelineData = store.getState().timelineModule.elementData[timelineId];
	// 	 const val = timelineData && timelineData.totalTime ?
	// 		timelineData.totalTime : this.getTimelineLoopTime() + this.getMinExtraTime();

	// 	 return val;
	//  }

	/**
	 * @static
	 * @returns the minimum extra time displayed to the right of the loop in the timeline
	 * @memberof TimelineEditorUtilities
	 */
	static getMinExtraTime() 
	{
		//TODO: This should probably be set within some global settings...
		return 1;
	}
}
